class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this))
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden')
    } else if (this.input.value.length === 0  && !resetIsHidden) {
      this.resetButton.classList.add('hidden')
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a')
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

document.addEventListener('submit', function(event) {
  if (event.target && event.target.matches('form[action*="/search"]')) {
    const input = event.target.querySelector('input[type="search"]');
    if (!input || !input.value) return;
    
    // Skip if they selected a dropdown item (handled natively by predictive search)
    const predictiveSearch = event.target.closest('predictive-search');
    if (predictiveSearch && predictiveSearch.querySelector('[aria-selected="true"] a')) {
      return;
    }

    event.preventDefault(); // Intercept the submission

    const searchTerm = input.value;
    const cleanTermNoSpace = searchTerm.replace(/\s+/g, '');
    const addSpaceTerm = searchTerm.replace(/(\d+(?:\.\d+)?)([a-zA-Z]+)/g, '$1 $2');
    const addSpaceTerm2 = searchTerm.replace(/([a-zA-Z]+)(\d+)/g, '$1 $2');
    const addHyphenTerm = searchTerm.replace(/([a-zA-Z]+)(\d+)/g, '$1-$2');
    const dimensionTerm = searchTerm.replace(/\*/g, ' x ');
    
    const queriesToTry = [];
    
    const addOhmVariations = (term) => {
      const vars = [];
      const lower = term.toLowerCase();
      if (lower.includes('ohm')) {
        vars.push(term.replace(/ohms?/gi, 'Ω'));
        vars.push(term.replace(/ohms?/gi, ' Ω'));
      }
      if (lower.includes('Ω') || lower.includes('omega')) {
        vars.push(term.replace(/Ω|omega/gi, 'ohm'));
        vars.push(term.replace(/Ω|omega/gi, ' ohm'));
        vars.push(term.replace(/Ω|omega/gi, 'ohms'));
        vars.push(term.replace(/Ω|omega/gi, ' ohms'));
      }
      return vars;
    };

    const registerQuery = (query) => {
      const trimmed = query.trim();
      if (trimmed.length > 0) {
        queriesToTry.push(trimmed);
        if (trimmed.includes(' ')) {
          queriesToTry.push(`"${trimmed}"`);
        }
      }
    };

    registerQuery(searchTerm);
    
    if (searchTerm.includes('*')) {
       registerQuery(dimensionTerm);
    }
    
    if (cleanTermNoSpace !== searchTerm.trim() && cleanTermNoSpace.length > 1) {
       registerQuery(cleanTermNoSpace);
    }
    
    if (addSpaceTerm !== searchTerm.trim() && addSpaceTerm !== cleanTermNoSpace && addSpaceTerm.length > 1) {
       registerQuery(addSpaceTerm);
    }

    if (addSpaceTerm2 !== searchTerm.trim() && addSpaceTerm2 !== cleanTermNoSpace && addSpaceTerm2.length > 1) {
       registerQuery(addSpaceTerm2);
    }

    if (addHyphenTerm !== searchTerm.trim() && addHyphenTerm.length > 1) {
       registerQuery(addHyphenTerm);
    }

    const currentQueries = [...queriesToTry];
    currentQueries.forEach(q => {
      const cleanQ = q.replace(/"/g, '');
      const vars = addOhmVariations(cleanQ);
      vars.forEach(v => registerQuery(v));
    });
    
    const words = searchTerm.trim().split(/\s+/);
    if (words.length > 1) {
      registerQuery(words[0]);
      const wordVars = addOhmVariations(words[0]);
      wordVars.forEach(v => registerQuery(v));
    }
    
    const uniqueQueries = [...new Set(queriesToTry)];

    // Find the base URL for routing
    const routesUrl = (window.routes && window.routes.predictive_search_url) ? window.routes.predictive_search_url : (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'search/suggest' : '/search/suggest');
    
    const fetchQuery = (query) => {
      let finalQuery = query;
      if (!finalQuery.endsWith('"')) finalQuery += '*';
      return fetch(`${routesUrl}?q=${encodeURIComponent(finalQuery)}&resources[type]=product&resources[options][fields]=title,product_type,vendor,variants.title,variants.sku&section_id=predictive-search`)
        .then(response => {
          if (!response.ok) throw new Error();
          return response.text();
        });
    };

    // Sequential Promise Execution to find the winning query
    let currentPromise = fetchQuery(uniqueQueries[0]);
    let winningQuery = uniqueQueries[0];
    
    for (let i = 1; i < uniqueQueries.length; i++) {
       currentPromise = currentPromise.then(text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          if (doc.querySelector('a[href*="/products/"]')) {
            return text; // Found results, pass it down to skip the rest
          }
          winningQuery = uniqueQueries[i];
          return fetchQuery(uniqueQueries[i]);
       }).catch(() => {
          winningQuery = uniqueQueries[i];
          return fetchQuery(uniqueQueries[i]);
       });
    }

    // Once the chain completes, redirect to the main search page with the winning query
    currentPromise.then(() => {
       let finalQuery = winningQuery;
       if (!finalQuery.endsWith('"')) finalQuery += '*';
       
       const rootUrl = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes.root : '/';
       const searchUrl = (window.routes && window.routes.search_url) ? window.routes.search_url : rootUrl + 'search';
       window.location.href = `${searchUrl}?q=${encodeURIComponent(finalQuery)}&options[prefix]=last&options[fields]=title,vendor,product_type,variants.title,variants.sku&type=product`;
    }).catch(() => {
       let finalQuery = uniqueQueries[0];
       if (!finalQuery.endsWith('"')) finalQuery += '*';
       
       const rootUrl = (window.Shopify && window.Shopify.routes) ? window.Shopify.routes.root : '/';
       const searchUrl = (window.routes && window.routes.search_url) ? window.routes.search_url : rootUrl + 'search';
       window.location.href = `${searchUrl}?q=${encodeURIComponent(finalQuery)}&options[prefix]=last&options[fields]=title,vendor,product_type,variants.title,variants.sku&type=product`;
    });
  }
}, true);
